/* eslint-disable no-nested-ternary */
import { useTranslator } from '@bluebeam/react-i18n';
import classnames from 'classnames';
import BouncingSpinner from 'components/BouncingSpinner';
import ECSEditor from 'components/editor/ECSEditor';
import ECSEditorPage from 'components/editor/ECSEditorPage';
import TriangleArrow from 'components/Icons/TriangleArrow';
import { ModalStaticProperties } from 'components/ModalPortal/ModalPortal';
import ModalStdHeader from 'components/modals/ModalStdHeader';
import Toolbar from 'components/viewer/toolbars/Toolbar';
import ZoomControls from 'components/viewer/toolbars/ZoomControls';
import AppStoreContext from 'contexts/AppStoreContext';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import download from 'images/icons/download.svg';
import React, { useContext, useEffect, useState } from 'react';
import { Doc } from 'records/Doc';
import { Submittal, SubmittalId } from 'records/Submittal';
import { downloadSubmittalAttachment } from 'services/submittalService';
import { downloadFileWithAnchor, getDateString } from 'utils/general';
import { WithAppStoreProps } from 'utils/withAppStore';
import SubmittalViewerSidebar from './SubmittalViewerSidebar';

interface Props extends WithAppStoreProps {
  doc: Doc;
  submittalId: SubmittalId;
  showToolbar: boolean;
}

const SubmittalViewerModal: React.FC<Props> & ModalStaticProperties = (
  props
) => {
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [downloading, setDownloading] = useState<boolean>(false);
  const { state: appState, actions } = useContext(AppStoreContext);
  const { translate } = useTranslator(['submittalsPage', 'buttons']);
  const { [FeatureFlag.ViewerToolbar]: isViewerToolbarEnabled } =
    useFeatureFlags();

  useEffect(() => {
    const { doc } = props;
    if (doc) {
      loadPages();
    }
    setDataLoaded(true);
  }, []);

  const loadPages = async (): Promise<void> => {
    await actions.getPages(doc.documentid);
  };

  const updatePageIndex = (nextPageIndex: number): void => {
    const { doc } = props;
    const { pages } = appState;

    const docPagesCount = pages.filter(
      (p) => p.documentid === doc.documentid
    ).size;

    if (nextPageIndex >= 0 && nextPageIndex !== docPagesCount) {
      setPageIndex(nextPageIndex);
    }
  };

  const downloadPDF = async (): Promise<void> => {
    try {
      setDownloading(true);
      const { doc } = props;
      if (doc) {
        const url = await downloadSubmittalAttachment(
          doc.projectid,
          doc.documentid
        );

        const filename = `${doc.filename.replace(
          /\.[^/.]+$/,
          ''
        )}-${getDateString()}.pdf`;

        await downloadFileWithAnchor(url, filename);
      }
    } catch (err) {
      console.log('SubmittalViewerModal downloadPDF Error', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadClick = async (): Promise<void> => {
    downloadPDF();
  };

  const { doc, submittalId, showToolbar } = props;
  const { pages } = appState;

  const docPages = pages
    .valueSeq()
    .filter((p) => doc && p.documentid === doc.documentid)
    .toList();

  const page = docPages.find((p) => p.pageIndex === pageIndex);
  const firstPage = pageIndex === 0;
  const lastPage = pageIndex === docPages.size - 1;
  const submittal = appState.submittals.get(submittalId, new Submittal());

  // see if the submittal has a status of approved, and if so, put the viewer in 'view only' mode.
  const isApproved = submittal.status.kind === 'SUB_APPROVED';
  const editorContainerId = 'doc-viewer-container';

  return (
    <div id="sub-viewer-modal" className="column h100">
      <ModalStdHeader text={doc ? doc.filename : ''} />
      {/* The 'flex: 1' style below is to fix a bug with Safari */}
      <div className="pt7 grow-1" style={{ flex: '1' }}>
        <div
          className="row h100"
          style={{ backgroundColor: 'rgb(195, 197, 201)' }}
        >
          <div className="grow-1">
            {dataLoaded && page ? (
              <ECSEditor containerId={editorContainerId} isModal isScrollable>
                <ECSEditorPage
                  key={page.pageid}
                  doc={doc}
                  page={page}
                  withMarkups
                  isInteractive={showToolbar}
                />
                {showToolbar && !isApproved && isViewerToolbarEnabled && (
                  <Toolbar />
                )}
                <ZoomControls />
              </ECSEditor>
            ) : null}
          </div>
          <SubmittalViewerSidebar submittalId={submittalId} />
        </div>
      </div>
      <div className="shrink-0 bgd-white row justify-between mx3 my2">
        <div className="flex-center mt1" style={{ width: '200px' }}>
          {downloading ? (
            <div className="flex-center">
              <BouncingSpinner />
            </div>
          ) : doc ? (
            <button
              id="sub-viewer-modal-download-btn"
              className="btn-sm btn-link mr3 ml0"
              onClick={handleDownloadClick}
            >
              <img className="mr1" src={download} alt="trash icon" />
              {translate('buttons:download')}
            </button>
          ) : (
            <div className="btn-sm btn-link mr3 ml0" />
          )}
        </div>
        {doc ? (
          <div className="row items-center color-gray-6">
            <div
              id="sub-viewer-modal-prev-page"
              className={classnames(
                'rotate-180',
                firstPage ? 'cursor-not-allowed' : 'cursor-pointer'
              )}
              onClick={(): void => updatePageIndex(pageIndex - 1)}
            >
              <TriangleArrow
                style={{
                  fill: `var(--${firstPage ? 'mid-grey-1' : 'black-2'})`,
                }}
              />
            </div>
            <span className="mx4">
              {translate('formLabels:pagesPagination', {
                currentPage: pageIndex + 1,
                allPages: docPages.size,
              })}
            </span>
            <div
              id="sub-viewer-modal-next-page"
              className={lastPage ? 'cursor-not-allowed' : 'cursor-pointer'}
              onClick={(): void => updatePageIndex(pageIndex + 1)}
            >
              <TriangleArrow
                style={{
                  fill: `var(--${lastPage ? 'mid-grey-1' : 'black-2'})`,
                }}
              />
            </div>
          </div>
        ) : (
          <div className="row items-center color-gray-6" />
        )}
        <div className="noop-spacer" style={{ width: '200px' }} />
      </div>
    </div>
  );
};

SubmittalViewerModal.modalStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  content: {
    padding: '0',
    margin: '0',
    left: '1.5rem',
    top: '1.5rem',
    right: '1.5rem',
    bottom: '1.5rem',
    borderRadius: '8px',
    border: 'none',
  },
};

export default SubmittalViewerModal;
